// src/pages/Education/Preschools.jsx
// Экран АРМ «Образование — дошкольные организации» (UC.13.02).
// Журнал (antd Table, серверная пагинация/фильтры через api.fetchPreschools) + модальная
// форма создания. Двуязычность RU/KK: язык приходит из app-уровня через prop `lang`
// (интеграция с общим i18n/переключателем шапки); смена языка перерисовывает подписи
// без перезагрузки и без потери введённого в форме.
import React from 'react';
import { Table, Input, Select, Button, notification } from 'antd';
import { fetchPreschoolMeta, fetchPreschools } from './api';
import { FILL_STATES, STATE_I18N_KEY } from './occupancy';
import { buildColumns } from './columns';
import PreschoolFormModal from './PreschoolFormModal';
import { t as translate } from './i18n';
import './Preschools.css';
import BasicLayout from '../../components/layout/BasicLayout';

const { Option } = Select;
const PER = 15;

export default class Preschools extends React.Component {
  static defaultProps = { lang: 'ru' };

  state = {
    meta: null,
    rows: [],
    total: 0,
    page: 1,
    loading: false,
    modalOpen: false,
    filters: { q: '', locality: '', type: '', fill_state: '' },
    resetKey: 0, // меняется при сбросе, чтобы перемонтировать (очистить) поле поиска
  };

  t = (k) => translate(this.props.lang, k);

  componentDidMount() {
    fetchPreschoolMeta().then((meta) => {
      this.setState({ meta }, this.loadList);
    });
  }

  loadList = () => {
    const { page, filters } = this.state;
    this.setState({ loading: true });
    fetchPreschools({ page, per: PER, ...filters })
      .then(({ rows, total }) => this.setState({ rows, total, loading: false }))
      .catch(() => this.setState({ loading: false }));
  };

  // Любое изменение поиска/разреза сбрасывает страницу на 1 и перезапрашивает журнал.
  setFilter = (key, value) => {
    this.setState((s) => ({ page: 1, filters: { ...s.filters, [key]: value } }), this.loadList);
  };

  onPageChange = (page) => this.setState({ page }, this.loadList);

  resetFilters = () => {
    this.setState(
      (s) => ({
        page: 1,
        resetKey: s.resetKey + 1,
        filters: { q: '', locality: '', type: '', fill_state: '' },
      }),
      this.loadList
    );
  };

  openModal = () => this.setState({ modalOpen: true });
  closeModal = () => this.setState({ modalOpen: false });

  onCreated = (created) => {
    this.closeModal();
    // Новая запись доступна в основной части (дэшборд/карта/сводная). Возвращаемся на
    // 1-ю страницу и перезапрашиваем — запись окажется в начале журнала.
    this.setState({ page: 1 }, this.loadList);
    notification.success({
      message: this.t('toastOk').replace('{n}', created.name),
      description: this.t('toastSub'),
      placement: 'bottomRight',
      duration: 3.6,
      // Точка интеграции: сюда можно добавить btn со ссылкой на «Действия пользователей».
    });
  };

  render() {
    const { lang } = this.props;
    const { meta, rows, total, page, loading, modalOpen, filters, resetKey } = this.state;
    if (!meta) return <div className="ddo-page ddo-loading">…</div>;

    const t = this.t;
    const canCreate = meta.permissions && meta.permissions.can_create;
    const localities = meta.dictionaries.locality || [];

    return (
      <BasicLayout>
        <div className="ddo-page">
          <div className="ddo-page-head">
            <div>
              <h1 className="ddo-title">{t('title')}</h1>
            </div>
            {canCreate && (
              <Button type="primary" onClick={this.openModal}>
                {t('create')}
              </Button>
            )}
          </div>

          <div className="ddo-filters">
            <div className="ddo-fl ddo-fl--grow">
              <label>{t('fSearch')}</label>
              <Input.Search
                key={resetKey}
                allowClear
                placeholder={t('phSearch')}
                onSearch={(v) => this.setFilter('q', v)}
                onChange={(e) => {
                  if (e.target.value === '') this.setFilter('q', '');
                }}
              />
            </div>
            <div className="ddo-fl">
              <label>{t('fLoc')}</label>
              <Select
                style={{ width: 200 }}
                value={filters.locality}
                onChange={(v) => this.setFilter('locality', v)}
              >
                <Option value="">{t('allLoc')}</Option>
                {localities.map((o) => (
                  <Option key={o.v} value={o.v}>
                    {o[lang]}
                  </Option>
                ))}
              </Select>
            </div>
            <div className="ddo-fl">
              <label>{t('fType')}</label>
              <Select
                style={{ width: 180 }}
                value={filters.type}
                onChange={(v) => this.setFilter('type', v)}
              >
                <Option value="">{t('allType')}</Option>
                {meta.dictionaries.type.map((o) => (
                  <Option key={o.v} value={o.v}>
                    {o[lang]}
                  </Option>
                ))}
              </Select>
            </div>
            <div className="ddo-fl">
              <label>{t('fState')}</label>
              <Select
                style={{ width: 180 }}
                value={filters.fill_state}
                onChange={(v) => this.setFilter('fill_state', v)}
              >
                <Option value="">{t('allState')}</Option>
                {FILL_STATES.map((s) => (
                  <Option key={s} value={s}>
                    {t(STATE_I18N_KEY[s])}
                  </Option>
                ))}
              </Select>
            </div>
            <div className="ddo-fl">
              <Button onClick={this.resetFilters}>{t('reset')}</Button>
            </div>
          </div>

          <Table
            className="ddo-table"
            rowKey="id"
            size="middle"
            loading={loading}
            columns={buildColumns({ t, lang, meta })}
            dataSource={rows}
            locale={{ emptyText: t('emptyList') }}
            pagination={{
              current: page,
              pageSize: PER,
              total,
              showTotal: (tot) => t('found').replace('{n}', tot),
              onChange: this.onPageChange,
            }}
          />

          <PreschoolFormModal
            visible={modalOpen}
            lang={lang}
            meta={meta}
            t={t}
            onCancel={this.closeModal}
            onCreated={this.onCreated}
          />
        </div>
      </BasicLayout>
    );
  }
}
